import { action_ids, eActionType, getActionId } from '@common/actions/action-types';
import { ActionShared } from '@common/actions/action-version-implementation/action-shared';
import { EActionsVersions } from '@common/actions/actions-version';
import { eActionTemplate } from '@common/request_enums';
import { TranslateService } from '@ngx-translate/core';

type TActionsValues = (typeof actions)[ keyof typeof actions ][ 'values' ];

type icon_type = string[];

export interface IPreparedActionVersion0
{
    template_id: eActionTemplate;
    action_id: action_ids;
    version: EActionsVersions;
    values: TActionsValues | IActionsValues;
}

export interface IActionsValues
{
    transaction?: TActionsValues;
    exchange?: TActionsValues;
    action?: TActionsValues;
}

interface ITransactionValuesVersion0
{
    amount: string;
    symbol: string;
    icon: icon_type;
    src_label: string;
    src_address: string;
    dst_label: string;
    dst_address: string;
}

interface IExchangeValuesVersion0
{
    market_name: string;
    market_icon: icon_type;
    pay_label: string;
    pay_amount: string;
    pay_icon: icon_type;
    pay_symbol: string;
    get_label: string;
    get_amount: string;
    get_icon: icon_type;
    get_symbol: string;
}

interface IActionValuesVersion0
{
    type: eActionType;
    wallet_name?: string;
    action_icon?: icon_type;
    action_info: string | string[];
}

const unknownTokenIcon = (icons: string[]) =>
{
    if (!icons.length)
    {
        return [ '/assets/images/symbols/token-unknown.svg' ];
    }
    return icons;
};

const unknownExchangeIcon = (icons: string[]) =>
{
    if (!icons.length)
    {
        return [ '/assets/images/exchanges/exchange-unknown.svg' ];
    }
    return icons;
};

export class ActionVersion0
{
    private version: EActionsVersions;
    private type: eActionType;
    private translate: TranslateService;

    constructor(version: EActionsVersions, type: eActionType, translate: TranslateService)
    {
        this.version = version;
        this.type = type;
        this.translate = translate;
    }

    public getPreparedAction(): IPreparedActionVersion0
    {
        const templateID = ActionShared.getTemplate(this.type);
        const action = actions[ this.type ];
        return {
            template_id: templateID,
            version: this.version,
            ...action,
        };
    }

    public async extractActionValues(info: IPreparedActionVersion0, ...args: string[]): Promise<IActionsValues>
    {
        const sourceValues = info.values;
        const destValues = {};

        for (const key of Object.keys(sourceValues))
        {
            const element = sourceValues[ key ];
            const value = await this.extractValues(element, ...args);

            if (Array.isArray(element))
            {
                const lastElement = element.pop();
                const isHandler = typeof lastElement === 'function';
                if (isHandler)
                {
                    destValues[ key ] = lastElement(value);
                    continue;
                }
            }
            destValues[ key ] = value;

        }
        const extractedValues: IActionsValues = {};
        switch (info.template_id)
        {
            case eActionTemplate.transaction: extractedValues.transaction = destValues as ITransactionValuesVersion0; break;
            case eActionTemplate.exchange: extractedValues.exchange = destValues as IExchangeValuesVersion0; break;
            case eActionTemplate.action: extractedValues.action = destValues as IActionValuesVersion0; break;
        }

        return extractedValues;
    }

    private async extractValues(element: string | number | string[], ...args): Promise<string | number | string[]>
    {
        let result;
        if (Array.isArray(element))
        {
            for (const [ index, item ] of element.entries())
            {
                if (typeof item === 'function')
                {
                    continue;
                }

                const extractedValue = await ActionShared.extractValue(item, this.translate, ...args);
                if (index === 0)
                {
                    result = extractedValue ? [ extractedValue ] : [];
                    continue;
                }
                result.push(extractedValue);
            }
        }
        else if (typeof element === 'number')
        {
            result = getActionId(element);
        }
        else
        {
            result = await ActionShared.extractValue(element, this.translate, ...args);
        }
        return result;
    }
}

const actions = {
    // now not support on the hid
    [ eActionType.SignMessage ]: {
        action_id: `${ getActionId(eActionType.SignMessage) }:{0}`,
        values: {
            type: eActionType.SignMessage,
            message: '{1}'
        }
    },
    [ eActionType.TransactionStart ]: {
        action_id: `${ getActionId(eActionType.TransactionStart) }:{0}`,
        values: {
            src_label: 'ACTIONS.TRANSACTION.SRC-TITLE',
            src_address: '{1}',
            dst_label: 'ACTIONS.TRANSACTION.DST-TITLE',
            dst_address: '{2}',
            amount: '{3}',
            symbol: '{4}',
            icon: [ '{5}', unknownTokenIcon ]
        } as ITransactionValuesVersion0
    },
    [ eActionType.ExchangeStart ]: {
        action_id: `${ getActionId(eActionType.ExchangeStart) }:{0}`,
        values: {
            market_name: '{1}',
            market_icon: [ '{6}', unknownExchangeIcon ],
            pay_label: 'ACTIONS.EXCHANGE.PAY-TITLE',
            pay_amount: '{2}',
            pay_icon: [ '{7}', unknownTokenIcon ],
            pay_symbol: '{3}',
            get_label: 'ACTIONS.EXCHANGE.GET-TITLE',
            get_amount: '{4}',
            get_icon: [ '{8}', unknownTokenIcon ],
            get_symbol: '{5}'
        } as IExchangeValuesVersion0
    },
    [ eActionType.ExchangeLoad ]: {
        action_id: `${ getActionId(eActionType.ExchangeLoad) }:{0}`,
        values: {
            type: eActionType.ExchangeLoad,
            action_info: [ '{1}', ':', 'ACTIONS.ACTION.EXCHANGE-LOAD', (val: string[]) => val.join(' ') ],
            action_icon: [ '{2}', unknownExchangeIcon ]
        } as IActionValuesVersion0
    },
    [ eActionType.BackupRemove ]: {
        action_id: `${ getActionId(eActionType.BackupRemove) }:{0}`,
        values: {
            type: eActionType.BackupRemove,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.BACKUP-REMOVE',
        } as IActionValuesVersion0
    },
    // deprecated action
    [ eActionType.BackupLoad ]: {
        action_id: `${ getActionId(eActionType.BackupLoad) }:{0}`,
        values: {
            type: eActionType.BackupLoad,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.BACKUP-LOAD',
        } as IActionValuesVersion0
    },
    [ eActionType.ExportPhrase ]: {
        action_id: `${ getActionId(eActionType.ExportPhrase) }:{0}`,
        values: {
            type: eActionType.ExportPhrase,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.EXPORT-PHRASE',
        } as IActionValuesVersion0
    },
    [ eActionType.ExportJson ]: {
        action_id: `${ getActionId(eActionType.ExportJson) }:{0}`,
        values: {
            type: eActionType.ExportJson,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.EXPORT-JSON',
        } as IActionValuesVersion0
    },
    [ eActionType.ExportKey ]: {
        action_id: `${ getActionId(eActionType.ExportKey) }:{0}`,
        values: {
            type: eActionType.ExportKey,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.EXPORT-KEY',
        } as IActionValuesVersion0
    },
    [ eActionType.WalletRemove ]: {
        action_id: `${ getActionId(eActionType.WalletRemove) }:{0}`,
        values: {
            type: eActionType.WalletRemove,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.WALLET-REMOVE',
        } as IActionValuesVersion0
    },
    [ eActionType.WalletUpdate ]: {
        action_id: `${ getActionId(eActionType.WalletUpdate) }:{0}`,
        values: {
            type: eActionType.WalletUpdate,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.WALLET-UPDATE',
        } as IActionValuesVersion0
    },
    [ eActionType.SecurityChange ]: {
        action_id: `${ getActionId(eActionType.SecurityChange) }`,
        values: {
            type: eActionType.SecurityChange,
            action_info: 'ACTIONS.ACTION.SECURITY-CHANGE',
        } as IActionValuesVersion0
    },
    // now not support on the hid
    [ eActionType.AddAddress ]: {
        action_id: `${ getActionId(eActionType.AddAddress) }:{0}`,
        values: {
            type: eActionType.AddAddress,
            wallet_name: '{1}',
            action_info: 'ACTIONS.ACTION.ADD-ADDRESS',
        } as IActionValuesVersion0
    },
    [ eActionType.AccountRemove ]: {
        action_id: `${ getActionId(eActionType.AccountRemove) }:{0}`,
        values: [
            { name: 'ACTIONS.ACTION.ACTION-TITLE', value: 'ACTIONS.ACTION.ACCOUNT-REMOVE' },
            { name: '--', value: '' }
        ]
    },
};
